'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useUser, useClerk } from '@clerk/nextjs';
import { Button } from '@/components/ui/button';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function DeleteAccountPage() {
  const router = useRouter();
  const { user } = useUser();
  const { signOut } = useClerk();
  const [confirm, setConfirm] = useState('');
  const [deleting, setDeleting] = useState(false);

  const CONFIRM_PHRASE = 'delete my account';

  async function handleDelete() {
    if (confirm !== CONFIRM_PHRASE || !user) return;
    setDeleting(true);

    try {
      // Clerk gestisce la cancellazione dell'identità utente.
      await user.delete();
      toast.success('Your account has been deleted. Contact support@lithosbase.app to remove any remaining workspace data.');
      await signOut();
      router.push('/');
    } catch (e) {
      setDeleting(false);
      toast.error(e instanceof Error ? e.message : 'Could not delete your account. Please try again.');
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-start justify-center py-16 px-4">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <AlertTriangle size={20} className="text-destructive" />
          <h1 className="text-xl font-bold text-destructive">Delete account</h1>
        </div>

        <div className="card-elevated p-6 flex flex-col gap-5">
          <div className="text-sm text-muted-foreground space-y-2">
            <p>
              This action will <strong className="text-foreground">permanently delete your account</strong> and
              sign you out. Workspaces you own and their data will remain until a full deletion is processed.
            </p>
            <p>
              To permanently delete all workspace data, email us at <strong className="text-foreground">support@lithosbase.app</strong> after
              submitting this form. We will process the deletion within 30 days per GDPR requirements.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              Type <strong>{CONFIRM_PHRASE}</strong> to confirm:
            </label>
            <input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              className="field-input"
            />
          </div>

          <div className="flex gap-2 pt-1">
            <Button
              onClick={handleDelete}
              disabled={deleting || confirm !== CONFIRM_PHRASE}
              className="bg-destructive hover:bg-destructive/90 text-white h-10 flex-1"
            >
              {deleting ? 'Deleting...' : 'Delete my account'}
            </Button>
            <Button variant="outline" onClick={() => router.back()} className="h-10">
              Cancel
            </Button>
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center mt-4">
          Changed your mind?{' '}
          <button onClick={() => router.back()} className="text-accent hover:underline">
            Go back
          </button>
        </p>
      </div>
    </div>
  );
}
